/**
 * Mobile Apps (HR + admin, 2026-10-07 Manak) — the latest APK of every AURA app,
 * read from the same release list the phones update from. Download, copy the
 * link to send to an employee, or show a QR to scan from their phone.
 */
import { useEffect, useRef, useState } from 'react';
import { Copy, Download, QrCode, X } from 'lucide-react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import { DeskState } from '@/pages/dashboard/roles/ProfileDeskParts';
import { shortDate, useMobileApps, type MobileApp } from './appOverviewData';

export function ForcedTag() {
  return <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700" title="Phones on an older version must update before they can carry on">FORCED</span>;
}

function CloseBtn({ className, children }: { className?: string; children: import('react').ReactNode }) {
  const { requestClose } = useModalClose();
  return <button type="button" aria-label="Close" onClick={requestClose} className={className}>{children}</button>;
}

function QrDialog({ app, onClose }: { app: MobileApp; onClose: () => void }) {
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    import('qrcode')
      .then((QRCode) => (QRCode.default ?? QRCode).toString(app.apkUrl, { type: 'svg', margin: 1, width: 240, errorCorrectionLevel: 'M' }))
      .then((s) => { if (alive) setSvg(s); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [app.apkUrl]);
  return <Modal open onClose={onClose} ariaLabel={`QR code for ${app.name}`} className="!max-w-xs !bg-white p-5 text-center max-sm:!max-w-none">
    <div>
      <div className="mb-3 flex items-center justify-between"><strong>{app.name} v{app.versionName}</strong><CloseBtn className="rounded p-1 hover:bg-gray-100 max-sm:-mr-2 max-sm:flex max-sm:min-h-[40px] max-sm:min-w-[40px] max-sm:items-center max-sm:justify-center"><X size={16} /></CloseBtn></div>
      {failed ? <p role="alert">Could not draw the QR code. Use Copy link instead.</p>
        : svg ? <div data-testid="qr-svg" className="mx-auto w-60" dangerouslySetInnerHTML={{ __html: svg }} />
        : <p role="status">Drawing QR…</p>}
      <p className="mt-3 text-sm text-gray-600">Scan with the employee&apos;s phone camera to download.</p>
    </div>
  </Modal>;
}

/** `download` is ignored for a link to another site, so open it in a new tab instead of leaving this page. */
function isCrossSite(url: string): boolean {
  try { return new URL(url, window.location.href).origin !== window.location.origin; } catch { return false; }
}

export function MobileAppsPage() {
  const apps = useMobileApps();
  const [qr, setQr] = useState<MobileApp | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (copiedTimer.current) clearTimeout(copiedTimer.current); }, []);
  const copy = async (app: MobileApp) => {
    try {
      await navigator.clipboard.writeText(app.apkUrl);
      setCopied(app.key);
      // The "Copied" label goes back to "Copy link" after a moment (and restarts if clicked again).
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(null), 2000);
    } catch {
      window.prompt('Copy this link', app.apkUrl);
    }
  };
  return <div className="mx-auto max-w-screen-xl space-y-6 p-4 max-sm:p-0 md:p-6">
    <header><h1 className="text-2xl font-semibold">Mobile Apps</h1><p className="text-gray-600">Download the latest app, or share the link / QR with an employee.</p></header>
    <DeskState resource={apps} />
    {apps.data && (apps.data.length === 0
      ? <p className="rounded-xl border border-gray-200 bg-white p-5">No app releases are published yet.</p>
      : <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,260px),1fr))] gap-4">
        {apps.data.map((app) => <section key={app.key} aria-label={app.name} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-2"><h2 className="text-lg font-semibold">{app.name}</h2>{app.forced && <ForcedTag />}</div>
          <p className="mt-1 text-sm text-gray-600">v{app.versionName}{app.releasedAt ? ` · ${shortDate(app.releasedAt)}` : ''}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a href={app.apkUrl} download {...(isCrossSite(app.apkUrl) ? { target: '_blank', rel: 'noopener noreferrer' } : {})} className="inline-flex items-center gap-1 rounded bg-green-800 px-3 py-1.5 text-sm text-white max-sm:min-h-[40px]"><Download size={14} /> Download</a>
            <button onClick={() => copy(app)} className="inline-flex items-center gap-1 rounded border px-3 py-1.5 text-sm max-sm:min-h-[40px]"><Copy size={14} /> {copied === app.key ? 'Copied' : 'Copy link'}</button>
            <button onClick={() => setQr(app)} className="inline-flex items-center gap-1 rounded border px-3 py-1.5 text-sm max-sm:min-h-[40px]"><QrCode size={14} /> QR</button>
          </div>
        </section>)}
      </div>)}
    {qr && <QrDialog app={qr} onClose={() => setQr(null)} />}
  </div>;
}
